
import React from 'react';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { Button, Stack } from '@mui/material';
import { useFeedbackCrud } from '@modules/Feedback/hooks/useFeedbackCrud';
import { MeetingFeedbackForm } from '@modules/Feedback/MeetingFeedbackForm';


type FormCrud = Feedback.Crud;

type Props = {
    onClose: () => void;
    meetingId: number;
    defaultValues?: Partial<FormCrud>;
    feedbackId?: number;
};

export const FeedbackForm: React.FC<Props> = ({
  onClose,
  meetingId,
  defaultValues,
  feedbackId,
}) => {
  const { createFeedback, editFeedback } = useFeedbackCrud();

  const methods = useForm<FormCrud>({
    defaultValues: defaultValues as FormCrud | undefined,
  });

  React.useEffect(() => {
    if (defaultValues) {
      methods.reset(defaultValues as FormCrud);
    }
  }, [defaultValues, methods]);

  const onSubmit = React.useCallback<SubmitHandler<FormCrud>>(
    async (data) => {
      try {
        if (feedbackId) {
          await editFeedback.mutateAsync({
            id: feedbackId,
            meeting: meetingId,
            ...data,
          });
        } else {
          await createFeedback.mutateAsync({
            meeting: meetingId,
            ...data,
          });
        }
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
    [createFeedback, editFeedback, meetingId, feedbackId, onClose],
  );

  const { handleSubmit, formState } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <MeetingFeedbackForm/>
        <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ p: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={formState.isSubmitting}>
            {feedbackId ? 'Save' : 'Confirm'}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};
